import React, { useEffect, useRef, useState } from "react";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Send } from "lucide-react";
import { sendChat, getCoachHistory, getRecommendations } from "../lib/api";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { BlobSticker, StarSticker } from "../components/DecorativeShapes";

const SUGGESTIONS = [
  "Why am I so tired today?",
  "Ideas for my luteal phase",
  "How can I sleep deeper tonight?",
  "What does my skin need this week?",
];

/* Light markdown renderer for AI replies \u2014 supports **bold**, *italic*, and bullet lines */
function escapeHtml(s) {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}
function renderInline(text) {
  if (!text) return "";
  let html = escapeHtml(text);
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/(^|\s)\*(?!\s)([^*\n]+?)\*(?=\s|$)/g, '$1<em>$2</em>');
  html = html.replace(/^\s*[-\u2022]\s+/gm, '\u2022\u00a0');
  return html;
}

export default function CoachPage({ profile }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [recs, setRecs] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const [hist, r] = await Promise.all([getCoachHistory(), getRecommendations()]);
        const transformed = [];
        hist.forEach((h) => {
          transformed.push({ role: "user", content: h.user_message, id: `${h.id}-u` });
          if (h.assistant_message) transformed.push({ role: "assistant", content: h.assistant_message, id: `${h.id}-a` });
        });
        setMessages(transformed);
        setRecs(r);
      } catch {}
    })();
  }, []);

  useEffect(() => { scrollRef.current?.scrollTo?.({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [messages, sending]);

  const send = async (text) => {
    const content = (text || input).trim();
    if (!content || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content, id: `local-${Date.now()}` }]);
    setSending(true);
    try {
      const history = messages.map((m) => ({ role: m.role, content: m.content }));
      const res = await sendChat(content, history);
      const reply = res.reply || "\u2014";
      setMessages((m) => [...m, { role: "assistant", content: reply, id: `local-a-${Date.now()}` }]);
      if (!res.ok) toast.error("Knya is having a soft pause. Try again.");
    } catch {
      toast.error("Could not reach Knya.");
    }
    setSending(false);
  };

  const firstName = (profile?.name || "").split(" ")[0];

  return (
    <div className="flex flex-col h-screen kanya-page-pad-bottom" data-testid="coach-page">
      <PageHeader
        eyebrow="Companion"
        title="Talk to Knya"
        subtitle={firstName ? `Here for you, ${firstName}.` : "Your calm companion."}
      />

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 pb-4 scrollbar-thin" data-testid="coach-chat-thread">
        {messages.length === 0 && !sending && (
          <div className="surface-plum editorial-shadow relative overflow-hidden rounded-[28px] p-6">
            <BlobSticker size={110} color="#E5C99D" className="absolute -top-10 -right-10 float-slow opacity-90" />
            <StarSticker size={32} color="#E5A8C0" className="absolute top-5 right-20 float-mid" />
            <p className="relative text-[10px] uppercase tracking-[0.28em] opacity-70 font-semibold">Hello</p>
            <p className="relative font-editorial text-[30px] leading-[1.04] mt-2 max-w-[280px]">
              Hi{firstName ? `, ${firstName}` : ""} — ask me anything.
            </p>
            <p className="relative text-sm opacity-80 mt-3 font-body max-w-[300px]">
              I translate your cycle, mood, sleep and skin into gentle, practical guidance. Calm, never clinical.
            </p>
          </div>
        )}

        {messages.length === 0 && !sending && recs?.items?.length > 0 && (
          <div className="mt-3 surface-cream plush-shadow-sm rounded-[24px] p-5 relative overflow-hidden">
            <BlobSticker size={60} color="#E5A8C0" className="absolute -top-6 -right-6 opacity-70" />
            <p className="relative text-[10px] uppercase tracking-[0.28em] font-semibold opacity-60">For today</p>
            <ul className="relative mt-2 space-y-2">
              {recs.items.map((r, idx) => (
                <li key={idx} className="flex items-start gap-3 text-sm font-body">
                  <span className="chip chip-mint shrink-0 !py-1 !px-2">{idx + 1}</span>
                  <span>{r}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="space-y-3 mt-3">
          <AnimatePresence initial={false}>
            {messages.map((m) => (
              <motion.div
                key={m.id}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                data-testid={`coach-message-${m.role}`}
              >
                <div
                  className={`max-w-[82%] px-4 py-3 rounded-3xl text-sm leading-relaxed whitespace-pre-wrap font-body ${
                    m.role === "user"
                      ? "surface-plum rounded-br-md"
                      : "surface-ivory rounded-bl-md border border-[hsl(var(--border))]"
                  }`}
                  dangerouslySetInnerHTML={{ __html: renderInline(m.content) }}
                />
              </motion.div>
            ))}
          </AnimatePresence>
          {sending && (
            <div className="flex justify-start" data-testid="coach-typing-indicator">
              <div className="surface-ivory border border-[hsl(var(--border))] rounded-3xl rounded-bl-md px-4 py-3 inline-flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 bg-[hsl(var(--plum))] rounded-full animate-pulse" />
                <span className="w-1.5 h-1.5 bg-[hsl(var(--plum))] rounded-full animate-pulse" style={{ animationDelay: "120ms" }} />
                <span className="w-1.5 h-1.5 bg-[hsl(var(--plum))] rounded-full animate-pulse" style={{ animationDelay: "240ms" }} />
              </div>
            </div>
          )}
        </div>
      </div>

      {messages.length === 0 && (
        <div className="px-5 pb-2">
          <div className="flex gap-2 overflow-x-auto scrollbar-thin pb-2">
            {SUGGESTIONS.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => send(s)}
                className="chip chip-cream whitespace-nowrap !py-2 !px-4 border border-[hsl(var(--border))]"
                data-testid="coach-suggested-prompt-chip"
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="px-5 pb-3 bg-transparent" style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 90px)" }}>
        <div className="mt-3 flex items-center gap-2">
          <Input
            placeholder="Tell Knya how you feel…"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            className="h-12 bg-[hsl(var(--card))] rounded-full px-5"
            data-testid="coach-message-input"
          />
          <Button onClick={() => send()} disabled={sending || !input.trim()} className="h-12 w-12 rounded-full p-0 bg-[hsl(24_24%_12%)] hover:bg-[hsl(24_30%_18%)] text-[hsl(36_50%_96%)]" data-testid="coach-send-button">
            <Send size={16} />
          </Button>
        </div>
      </div>
    </div>
  );
}
